import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Unplug, Loader2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { apiPost, apiDelete, apiGet } from '../../utils/api';

const CLIENT_ID = '80990e51-2ad9-4199-88e5-368123d17e36';
const TENANT_ID = 'e37dade8-4604-41e8-9615-48f0e0ca050c';
const SCOPES = 'Mail.Read offline_access';

function getRedirectUri() {
    return `${window.location.origin}/dashboard/email/callback`;
}

export default function ConnectOutlookButton({ connected, onStatusChange }) {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(false);

    const handleConnect = useCallback(() => {
        const state = crypto.randomUUID();
        sessionStorage.setItem('outlook_oauth_state', state);

        const redirectUri = getRedirectUri();
        const authUrl = new URL(`https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/authorize`);
        authUrl.searchParams.set('client_id', CLIENT_ID);
        authUrl.searchParams.set('response_type', 'code');
        authUrl.searchParams.set('redirect_uri', redirectUri);
        authUrl.searchParams.set('scope', SCOPES);
        authUrl.searchParams.set('state', state);
        authUrl.searchParams.set('response_mode', 'query');

        const width = 500, height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        window.open(
            authUrl.toString(),
            'outlook-auth',
            `width=${width},height=${height},left=${left},top=${top}`
        );
    }, []);

    useEffect(() => {
        function handleMessage(event) {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== 'outlook-oauth-callback') return;

            const { code, state, error } = event.data;
            const savedState = sessionStorage.getItem('outlook_oauth_state');
            sessionStorage.removeItem('outlook_oauth_state');

            if (error) {
                console.error('OAuth error:', error);
                return;
            }

            if (state !== savedState) {
                console.error('OAuth state mismatch');
                return;
            }

            if (code) {
                exchangeCode(code);
            }
        }

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    async function exchangeCode(code) {
        setLoading(true);
        try {
            const response = await apiPost('/outlook/connect', {
                code,
                redirect_uri: getRedirectUri(),
            });
            if (response.ok) {
                onStatusChange?.(true);
            }
        } catch (err) {
            console.error('Failed to connect Outlook:', err);
        } finally {
            setLoading(false);
        }
    }

    async function handleDisconnect() {
        setLoading(true);
        try {
            const response = await apiDelete('/outlook/disconnect');
            if (response.ok) {
                onStatusChange?.(false);
            }
        } catch (err) {
            console.error('Failed to disconnect:', err);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <button disabled className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
            }`}>
                <Loader2 size={18} className="animate-spin" />
                <span>Connecting...</span>
            </button>
        );
    }

    if (connected) {
        return (
            <button
                onClick={handleDisconnect}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                        ? 'bg-gray-700 hover:bg-red-900/30 text-gray-300 hover:text-red-400'
                        : 'bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600'
                }`}
            >
                <Unplug size={18} />
                <span>Disconnect Outlook</span>
            </button>
        );
    }

    return (
        <button
            onClick={handleConnect}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
            <Mail size={20} />
            <span>Connect Outlook</span>
        </button>
    );
}
